/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Persists Ops review decisions (Approve/Reject/Manual) for one or more Expense
 * Inventory sampling line items via the reviewExpenseInventory mutation.
 * Defaults to submit:false (save-only, per #1416718); passing { submit: true }
 * (Submit Review) finalizes every line's decision in one call instead. Converts
 * the frontend's 'Approve' value to the backend's expected 'Accept' at the
 * payload boundary, matching the established Inventory Request conversion
 * pattern.
 * Returns whether the save succeeded so the caller (ExpenseInventorySampling)
 * can roll back any optimistically-applied local decision on failure. A
 * refetch alone cannot be relied on for that: when a save-only call fails, its
 * transaction rolls back, so the refetch returns data identical to what's
 * already cached — Apollo doesn't emit an identical result, so the component
 * holding `expenseInventoryDetail` never re-renders and the rows are never
 * rebuilt from server state.
 * expectedCaseVersion is intentionally omitted: the optimistic-lock check
 * isn't needed for either the save-only or submit-review flow.
 * @module src/components/Inventory/expense/detail/ExpenseInventorySampling/hook/useReviewExpenseInventoryDecisions
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import REVIEW_EXPENSE_INVENTORY from '../../../../../../graphql/mutations/reviewExpenseInventory';
import {
  EXPENSE_INVENTORY_ASYNC_MESSAGES,
  EXPENSE_INVENTORY_CASE_STATUS,
  NOTIFY_TYPE,
  STALE_RECORD_MESSAGE,
  WORKFLOW_ACTION,
} from '../../../../../../util/constants';
import { isStaleRecordError } from '../../../../../../util/optimisticLock';
import { buildExpenseInventorySubmitHeader } from '../../../../../../util/expenseInventory';

interface DecisionToSave {
  uniqueId: string;
  sfid?: string;
  externalId?: string;
  decision: string;
}

interface SaveDecisionsOptions {
  submit?: boolean;
}

interface UseReviewExpenseInventoryDecisionsProps {
  expenseInventoryDetail: any;
  refetch: () => Promise<any>;
  openSnackBar: (type: string, message: string) => void;
}

const resolveWorkflowActionForSave = (decision: string): string =>
  decision === WORKFLOW_ACTION.APPROVE ? WORKFLOW_ACTION.ACCEPT : decision;

const useReviewExpenseInventoryDecisions = ({
  expenseInventoryDetail,
  refetch,
  openSnackBar,
}: UseReviewExpenseInventoryDecisionsProps) => {
  const [savingDecisionUniqueIds, setSavingDecisionUniqueIds] = useState<string[]>([]);
  const [reviewExpenseInventory, { loading: loadingSaveDecision }] = useMutation(
    REVIEW_EXPENSE_INVENTORY
  );

  // Best-effort resync after the mutation settles. Errors are swallowed: a failed
  // refetch must not be mistaken for a failed save (the mutation may have already
  // succeeded), and must not reject unhandled back out through onSaveDecisions.
  const refetchQuietly = useCallback(async (): Promise<void> => {
    try {
      await refetch();
    } catch {
      // Ignore - the next successful fetch will resync the UI.
    }
  }, [refetch]);

  const onSaveDecisions = useCallback(
    async (
      lines: DecisionToSave[],
      { submit = false }: SaveDecisionsOptions = {}
    ): Promise<boolean> => {
      // A line that hasn't synced to Salesforce yet (no sfid, only a local externalId) can't
      // be persisted as a decision by a save-only call — including it fails the whole batch.
      // Leave those lines out of save-only saves so the rest can still succeed; they remain
      // undecided locally until they sync and gain an sfid. Submit Review still sends every
      // line, since by then all parts must already be synced (backend enforces this).
      const eligibleLines = submit ? lines : lines.filter(line => line.sfid);
      const linesToSave = eligibleLines.filter(line => line.sfid || line.externalId);
      if (linesToSave.length === 0) {
        return true;
      }

      setSavingDecisionUniqueIds(linesToSave.map(line => line.uniqueId));
      try {
        const res = await reviewExpenseInventory({
          variables: {
            caseId: expenseInventoryDetail?.id,
            caseExternalId: expenseInventoryDetail?.caseExternalId,
            lineItems: linesToSave.map(line => ({
              usageId: line.sfid,
              usageExternalId: line.externalId,
              workflowAction: resolveWorkflowActionForSave(line.decision),
            })),
            submit,
            header: buildExpenseInventorySubmitHeader(expenseInventoryDetail),
          },
        });

        if (submit) {
          const status = res?.data?.reviewExpenseInventory?.status;
          const message =
            status === EXPENSE_INVENTORY_CASE_STATUS.CANCELLED
              ? EXPENSE_INVENTORY_ASYNC_MESSAGES.SUBMIT_REVIEW_CANCELLED
              : EXPENSE_INVENTORY_ASYNC_MESSAGES.SUBMIT_REVIEW_SUCCESS;
          openSnackBar(NOTIFY_TYPE.SUCCESS, message);
        }

        await refetchQuietly();
        return true;
      } catch (error) {
        // Show the failure first: the mutation itself rejected, so this message must
        // reach the user regardless of whether the refetch below succeeds.
        if (isStaleRecordError(error)) {
          openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
        } else {
          openSnackBar(
            NOTIFY_TYPE.ERROR,
            (error as Error)?.message || 'Error: Decision could not be saved.'
          );
        }

        // Covers the case where order-arming failed after the DB transaction already
        // committed (ORDER_START_FAILED) — the case may already be Submitted server-side
        // even though this call rejected. Best-effort only: the caller is responsible for
        // rolling back the optimistically-applied local decision, since a rolled-back
        // save-only transaction can refetch identical data that Apollo won't re-render.
        await refetchQuietly();
        return false;
      } finally {
        setSavingDecisionUniqueIds([]);
      }
    },
    [expenseInventoryDetail, reviewExpenseInventory, refetchQuietly, openSnackBar]
  );

  return { onSaveDecisions, loadingSaveDecision, savingDecisionUniqueIds };
};

export default useReviewExpenseInventoryDecisions;
